import { Injectable } from '@angular/core';
import { FirebaseError } from 'firebase/app';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom, Observable } from 'rxjs';
import { LoadingStatus } from 'src/app/core/domain/events/state_provider';

import UniqueEntityID from '../../../core/domain/unique_entity_id';
import { SortListType } from '../../../ui/dashboard/routes/questions/questions.service';
import { AuthenticationCommands } from '../../auth/domain/authentication_commands';
import { UserId } from '../../auth/domain/user';
import { DietitianId } from '../../dietitian/domain/dietitian';
import { InvitationTemplate } from '../../dietitian/domain/invitation_template/invitation-template';
import { Picture } from '../../dietitian/domain/picture/picture';
import { PictureRepository } from '../../dietitian/repositories/picture_repository';
import { Email } from '../../email/domain/email/email';
import { EmailCommands } from '../../email/domain/email/email_commands';
import { Template } from '../../email/domain/email/template';
import { LoggerRepository } from '../../logger/repositories/logger_repository';
import { DiaryTypeCommands } from '../../tracker/domain/diary_type/diary_type_commands';
import { Tracker, TrackerProps } from '../../tracker/domain/tracker/tracker';
import {
  TrackerCommands,
  TrackersWrapper,
} from '../../tracker/domain/tracker/tracker_commands';
import { AppointmentRepository } from '../repositories/appointment.repository';
import { GoalRepository } from '../repositories/goal.repository';
import {
  PatientRepository,
  ResultAlternativeLoginAction,
} from '../repositories/patient_repository';
import { Appointment } from './appointment/appointment';
import {
  AppointmentCreated,
  AppointmentDeleted,
  AppointmentUpdated,
} from './events/appointment/appointment.event';
import { AppointmentEventProvider } from './events/appointment/appointment_event_provider';
import {
  GoalChangeAchievement,
  GoalCreated,
  GoalUpdated,
} from './events/goal/goal.event';
import { GoalEventProvider } from './events/goal/goal_event.provider';
import {
  PatientActivated,
  PatientArchived,
  PatientCreated,
  PatientNextAppointmentUpdated,
  PatientSentOtp,
  PatientSignedIn,
  PatientSignedOut,
  PatientUpdated,
} from './events/patient.events';
import { PatientEventProvider } from './events/patient_event_provider';
import {
  PatientSelectionActivated,
  PatientSelectionArchived,
} from './events/patient_selection_event';
import { PatientSelectionEventProvider } from './events/patient_selection_event_provider';
import { Goal, GoalStatus } from './goal/goal';
import { Patient, PatientId, PatientProps } from './patient';
import {
  PatientExistingNumberException,
  PatientNotFoundException,
} from './patient_exceptions';
import { PatientSerie } from './patient_serie/patient_serie';
import { PatientSerieCommands } from './patient_serie/patient_serie_commands';
import { PatientSerieItem } from './patient_serie/patient_serie_item';
import { PatientState, PatientStateProvider } from './patient_state_provider';

@Injectable()
export class PatientCommands {
  constructor(
    private repository: PatientRepository,
    private appointmentRepository: AppointmentRepository,
    private pictureRepository: PictureRepository,
    private goalRepository: GoalRepository,
    private loggerRepository: LoggerRepository,
    private stateProvider: PatientStateProvider,
    private eventProvider: PatientEventProvider,
    private selectionEventProvider: PatientSelectionEventProvider,
    private appointmentEventProvider: AppointmentEventProvider,
    private goalEventProvider: GoalEventProvider,
    private serieCommands: PatientSerieCommands,
    private diaryTypeCommands: DiaryTypeCommands,
    private trackerCommands: TrackerCommands,
    private emailCommands: EmailCommands,
    private authenticationCommands: AuthenticationCommands,
    private toastr: ToastrService,
  ) {
    eventProvider.events$.subscribe((event) => {
      if (!event.notify) return;

      if (event instanceof PatientCreated) {
        if (event.entity) {
          this.toastr.success('Patient créé avec succès!', 'Succès');
        } else {
          this.toastr.error(
            'Une erreur est survenue lors de la création du patient.',
            'Erreur',
          );
        }
      } else if (event instanceof PatientUpdated) {
        this.toastr.success('Patient mis à jour');
      } else if (event instanceof PatientArchived) {
        this.toastr.success('Patient archivé');
      } else if (event instanceof PatientActivated) {
        this.toastr.success('Patient activé');
      } else if (event instanceof PatientNextAppointmentUpdated) {
        this.toastr.success('Nouveau rendez-vous pour le patient');
      }
    });

    goalEventProvider.events$.subscribe((event) => {
      if (event instanceof GoalCreated) {
        this.toastr.success('Objectif créé avec succès!', 'Succès');
      } else if (event instanceof GoalUpdated) {
        this.toastr.success('Objectif mis à jour');
      } else if (event instanceof GoalChangeAchievement) {
        this.toastr.success('Objectif - Achèvement mis à jour');
      }
    });
  }

  get appointmentEventProvider$() {
    return this.appointmentEventProvider.events$;
  }

  get patientEventProvider$(): PatientEventProvider {
    return this.eventProvider;
  }

  async updatePatient(
    patientId: string,
    patientProps: PatientProps,
  ): Promise<Patient> {
    this.stateProvider.setState(<PatientState>{
      loading: LoadingStatus.LOADING,
      entity: this.stateProvider.state.entity,
    });
    let patient: Patient | undefined = undefined;
    try {
      const oldPatient = await this.setPatientState(patientId);
      if (oldPatient === undefined) {
        throw new PatientNotFoundException();
      }
      patient = oldPatient.copyWith(patientProps);

      if (!oldPatient.archived && patient.archived) {
        // remove patient from group when archived
        patient = patient.updateGroupId();
        await this.update(patient, true);
        this.eventProvider.dispatch(new PatientArchived(patient));
      } else if (oldPatient.archived && !patient.archived) {
        await this.update(patient, true);
        this.eventProvider.dispatch(new PatientActivated(patient));
      } else {
        await this.update(patient, true);
      }
      return patient;
    } catch (error) {
      console.error('🔴 updatePatient', error);
      if (error instanceof Error) {
        this.stateProvider.setState(<PatientState>{
          loading: LoadingStatus.ERROR,
          message: error.message,
          entity: patient,
        });
      }
      throw error;
    }
  }

  getPatient(patientId: string): Promise<Patient> {
    return this.repository.load(patientId);
  }

  async setPatientState(patientId: string): Promise<Patient> {
    this.stateProvider.setState(<PatientState>{
      loading: LoadingStatus.LOADING,
    });
    try {
      const patient = await this.getPatient(patientId);
      this.stateProvider.setState(<PatientState>{
        loading: LoadingStatus.COMPLETE,
        entity: patient,
      });
      return patient;
    } catch (error) {
      if (error instanceof Error) {
        this.stateProvider.setState(<PatientState>{
          loading: LoadingStatus.ERROR,
          message: error.message,
        });
      }
      throw error;
    }
  }

  async update(patient: Patient, notify = true): Promise<Patient> {
    this.stateProvider.setState(<PatientState>{
      loading: LoadingStatus.LOADING,
      entity: this.stateProvider.state.entity,
    });
    try {
      if (patient.phoneNumber) {
        // check phone number
        const exists = await this.repository.existsForDietitianAndPhoneNumber(
          patient.dietId.toString(),
          patient.phoneNumber,
          patient.patientId.id.toString(),
        );

        if (exists) {
          throw new PatientExistingNumberException(patient.phoneNumber);
        }
      }

      await this.repository.save(patient);
      await this.loggerRepository.debugCloud('PatientCommand', 'update', {
        props: patient.props,
        id: patient.id.toString(),
      });
      this.stateProvider.setState(<PatientState>{
        loading: LoadingStatus.COMPLETE,
        entity: patient,
      });
      this.eventProvider.dispatch(new PatientUpdated(patient, notify));
      return patient;
    } catch (error) {
      if (error instanceof Error) {
        this.stateProvider.setState(<PatientState>{
          loading: LoadingStatus.ERROR,
          message: error.message,
          entity: patient,
        });
      }
      throw error;
    }
  }

  async create(patientProps: PatientProps): Promise<Patient> {
    patientProps.createdAt = new Date();

    this.stateProvider.setState(<PatientState>{
      loading: LoadingStatus.LOADING,
    });

    try {
      if (patientProps.phoneNumber) {
        // check phone number
        const exists = await this.repository.existsForDietitianAndPhoneNumber(
          patientProps.dietId.toString(),
          patientProps.phoneNumber,
        );
        if (exists) {
          throw new PatientExistingNumberException(patientProps.phoneNumber);
        }
      }

      const patient = await this.repository.create(
        Patient.create(patientProps),
      );

      this.stateProvider.setState(<PatientState>{
        loading: LoadingStatus.COMPLETE,
        entity: patient,
      });
      this.eventProvider.dispatch(new PatientCreated(patient));

      return patient;
    } catch (error) {
      if (error instanceof Error) {
        this.stateProvider.setState(<PatientState>{
          loading: LoadingStatus.ERROR,
          message: error.message,
        });
      }
      throw error;
    }
  }

  public async getActualWeight(patientId: string) {
    try {
      const serie = await this.serieCommands.getLastSerieItem(
        patientId,
        PatientSerie.Weight,
      );
      return serie?.value;
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  public async getActualWeight2(patientId: string) {
    try {
      const series = await this.serieCommands.getSerieWeightAndSizeItems(
        patientId,
        PatientSerie.Weight,
      );
      if (series && series.length > 0) {
        return series[0]?.value;
      }
    } catch (e) {
      console.error(e);
      throw e;
    }
    return undefined;
  }

  public async getActualSize(patientId: string) {
    const serie = await this.serieCommands.getLastSerieItem(
      patientId,
      PatientSerie.Size,
    );
    return serie?.value;
  }

  public async getActualSize2(patientId: string) {
    const series = await this.serieCommands.getSerieWeightAndSizeItems(
      patientId,
      PatientSerie.Size,
    );
    if (series && series.length > 0) {
      return series[0]?.value;
    }
    return undefined;
  }

  async setSerieItem(
    patientId: PatientId,
    patientUserId: UserId | undefined,
    timestamp: Date,
    value: number | undefined,
    serie: PatientSerie,
  ) {
    if (value) {
      await this.serieCommands.saveSerieItem({
        patientId,
        patientUserId,
        value,
        serie,
        timestamp,
        isPatientInput: false,
      });
    }
  }

  async deleteSerieItem(patient: Patient, serie: PatientSerieItem) {
    await this.serieCommands.deleteSerieItem(
      patient.id.toString(),
      serie.id.toString(),
    );
  }

  searchDietitianPatients(
    dietId: string,
    query: string,
    filterType: SortListType,
    groupId: string | null,
    resultsPerPage: number,
    page: number,
    sort: 'createdAt' | 'updatedAt' | 'lastActiveDate',
    algolia: boolean,
  ) {
    return this.repository.searchPatients(
      dietId,
      query,
      filterType == SortListType.Group ? groupId : null,
      filterType == SortListType.Archived,
      resultsPerPage,
      page,
      sort,
      algolia,
    );
  }

  getDietitianPatients(
    dietitianId: string,
    groupId: string | null,
    includeArchived = false,
  ): Promise<Patient[]> {
    return this.repository.getDietitianPatients(
      dietitianId,
      groupId,
      includeArchived,
    );
  }

  getDietitianPatientsWithUnreadDiaries(
    dietitianId: string,
    groupId: string | null,
    archived: boolean,
  ): Promise<Patient[]> {
    return this.repository.getDietitianPatientsWithUnreadDiaries(
      dietitianId,
      groupId,
      archived,
      30 * 86400,
    );
  }

  async markPatientDiariesAsRead(patientId: string): Promise<Patient> {
    const patient = await this.getPatient(patientId);
    return this.update(patient?.markDiariesAsRead(), false);
  }

  async markDietitianPatientsDiariesAsRead(
    dietitianId: string,
    groupId: string | null,
    archived: boolean,
  ): Promise<void> {
    // list patients
    const patients =
      await this.repository.getDietitianPatientsWithUnreadDiaries(
        dietitianId,
        groupId,
        archived,
        0,
      );

    // update patients
    const operations: Promise<unknown>[] = [];
    for (const patient of patients) {
      operations.push(this.update(patient.markDiariesAsRead(), false));
    }
    await Promise.all(operations);

    this.toastr.success('Journaux des patients marqués comme lus');
  }

  getPatientGroupPatients(
    dietitianId: string,
    groupId: string,
    includeArchived = true,
  ): Promise<Patient[]> {
    return this.repository.getPatientGroupPatients(
      dietitianId,
      groupId,
      includeArchived,
    );
  }

  public async sendInvitationEmailToPatient(
    dietitianId: string,
    patient: Patient | undefined,
    invitationTemplate: InvitationTemplate,
  ): Promise<Email | undefined> {
    if (!patient?.email) {
      this.toastr.error(
        'Vous devez saisir un email pour envoyer une invitation',
      );
      return;
    }

    await this.repository.createPatientInvitationNotification(patient);
    await this.repository.update(patient.id.toString(), {
      updatedAt: new Date(),
    } as PatientProps); // update patient update time
    await this.loggerRepository.debugCloud(
      'PatientCommand',
      'sendInvitationEmailToPatient',
      {
        props: patient.props,
        id: patient.id.toString(),
      },
    );

    return this.emailCommands.sendEmail({
      dietitianId,
      patientId: patient.id.toString(),
      to: [patient.email],
      template: Template.create({
        name: 'patient_application_invitation_v2',
        data: {
          preview: null,
          googlePlayLink:
            'https://play.google.com/store/apps/details?id=com.apparence.dietapp',
          appStoreLink:
            'https://apps.apple.com/fr/app/monsuividiet/id1463398409',
          phoneNumber: patient.phoneNumber ?? null,
          contentHtml: invitationTemplate.content.replace(/\r?\n/g, '<br />'),
          contentText: invitationTemplate.content,
        },
      }),
    });
  }

  public async registerPatientForSurvey(data: {
    dietitianId: string;
    surveyId: string;
    conversationId: string | undefined;
  }): Promise<string | undefined> {
    return firstValueFrom<string | undefined>(
      this.repository.registerPatientForSurvey(data),
    );
  }

  public async loginPatientForSurvey(data: {
    dietitianId: string;
    surveyId: string;
    code: string;
  }): Promise<string | undefined> {
    return firstValueFrom<string | undefined>(
      this.repository.loginPatientForSurvey(data),
    );
  }

  public async getAllSerieItems(
    patientId: string,
  ): Promise<PatientSerieItem[]> {
    return this.serieCommands.getAllItems(patientId);
  }

  public async getSerieWeightAndSizeItems(
    patientId: string,
    mode: 'weight' | 'size' | 'weightAndSize',
  ): Promise<PatientSerieItem[] | undefined> {
    return this.serieCommands.getSerieWeightAndSizeItems(patientId, mode);
  }

  async createPatientSerieItem(
    serie: PatientSerieItem,
  ): Promise<PatientSerieItem> {
    return this.serieCommands.createPatientSerieItem(serie);
  }

  async updatePatientSerieItem(
    serie: PatientSerieItem,
  ): Promise<PatientSerieItem> {
    return this.serieCommands.updatePatientSerieItem(serie);
  }

  async updateTrackersByGroup(patient: Patient) {
    if (patient.groupId && patient.groupId.length > 0) {
      await this.trackerCommands.deleteAllTrackersOfPatient(patient);
      let trackers: TrackersWrapper | undefined;
      for (const groupId of patient.groupId) {
        if (!trackers) {
          trackers = await this.trackerCommands.getTrackers(
            patient.dietId,
            groupId,
            undefined,
          );
          trackers.events = await this.diaryTypeCommands.findAllDiaryTypes(
            patient.dietId,
            groupId,
            undefined,
          );
        } else {
          // Trackers
          const newTrackers = await this.trackerCommands.getTrackers(
            patient.dietId,
            groupId,
            undefined,
          );
          for (const tracker of newTrackers.trackers) {
            const targets = trackers.trackers.filter(
              (t) =>
                t.name === tracker.name &&
                t.diaryType === tracker.diaryType &&
                t.type === tracker.type,
            );
            if (targets) {
              if (targets.length === 1) {
                if (tracker.activatedAt) {
                  targets[0].activatedAt = new Date();
                }
              } else if (targets.length === 0) {
                trackers.trackers.push(tracker);
              }
            }
          }

          // Events
          newTrackers.events = await this.diaryTypeCommands.findAllDiaryTypes(
            patient.dietId,
            groupId,
            undefined,
          );
          if (newTrackers.events && trackers.events) {
            for (const newEvent of newTrackers.events) {
              const expectedEvents = trackers.events.filter(
                (event) => event.name === newEvent.name,
              );
              if (newEvent.displayed) {
                if (!expectedEvents[0].displayed) {
                  expectedEvents[0].label = newEvent.label;
                  expectedEvents[0].displayed = newEvent.displayed;
                }
              }
            }
          }
        }
      }
      if (trackers) {
        await this.trackerCommands.createBatch(
          trackers.trackers.map((t) =>
            Tracker.create({
              ...t.props,
              dietitianId: DietitianId.create(
                new UniqueEntityID(patient.dietId),
              ),
              patientGroupId: patient.groupId,
              patientId: patient.patientId,
              patientUserId: patient.userId,
            } as TrackerProps),
          ),
        );

        if (trackers.events) {
          await this.diaryTypeCommands.createAllDiaryTypeToPatient(
            trackers.events,
            patient.id.toString(),
          );
        }
      }
    }
  }

  async archiveAllPatients(patients: Patient[] | undefined) {
    const cnt = new Map<string, number>();
    if (patients) {
      for (const patient of patients) {
        patient.archivedAt = new Date();
        if (patient.groupId) {
          for (const groupId of patient.groupId) {
            cnt.set(groupId, (cnt.get(groupId) ?? 0) + 1);
          }
        }
        patient.groupId = [];
      }
      await this.repository.updateBatch(patients);
      this.selectionEventProvider.dispatch(
        new PatientSelectionArchived(patients),
      );
    }
    return cnt;
  }

  async activeAllPatients(patients: Patient[] | undefined) {
    if (patients) {
      for (const patient of patients) {
        patient.archivedAt = undefined;
      }
      await this.repository.updateBatch(patients);
      this.selectionEventProvider.dispatch(
        new PatientSelectionActivated(patients),
      );
    }
  }

  async getAllArchivedPatients(dietitianId: string): Promise<Patient[]> {
    return this.repository.getDietitianArchivedPatients(dietitianId);
  }

  patientValueChanges(patientId: string): Observable<Patient | undefined> {
    return this.repository.patientValueChanges(patientId);
  }

  async findAllAppointment(patientId: string): Promise<Appointment[]> {
    return this.appointmentRepository.findAll(patientId);
  }

  async insertAppointment(
    patientId: string,
    date: Date,
    location: string | undefined,
    comment: string | undefined,
    duration: string | undefined,
  ): Promise<Appointment> {
    const appointment = Appointment.create({
      origin: 'msd',
      date,
      location,
      comment,
      duration,
    });
    const inserted = await this.appointmentRepository.create(
      patientId,
      appointment,
    );
    this.appointmentEventProvider.dispatch(new AppointmentCreated(inserted));
    return inserted;
  }

  async updateAppointment(
    patientId: string,
    appointment: Appointment,
  ): Promise<Appointment> {
    const updated = await this.appointmentRepository.update(
      patientId,
      appointment,
    );
    this.appointmentEventProvider.dispatch(new AppointmentUpdated(updated));
    return updated;
  }

  async deleteAppointment(patientId: string, appointment: Appointment) {
    await this.appointmentRepository.delete(
      patientId,
      appointment.id.toString(),
    );
    this.appointmentEventProvider.dispatch(new AppointmentDeleted(appointment));
  }

  async loadCurrentPatient(): Promise<Patient | undefined> {
    this.stateProvider.setState(<PatientState>{
      loading: LoadingStatus.LOADING,
      origin: 'PatientCommands.loadCurrentPatient',
    });
    try {
      const user = await this.authenticationCommands.loadCurrentPatientUser();
      if (user == null || user.phoneNumber == null) {
        this.stateProvider.setState(<PatientState>{
          loading: LoadingStatus.COMPLETE,
          origin: 'PatientCommands.loadCurrentPatient',
        });
        return undefined;
      }
      this.loggerRepository.who = user.phoneNumber.toString();
      const patient = await this.repository.loadByUserId(
        user.userId.id.toString(),
      );
      this.stateProvider.setState(<PatientState>{
        loading: LoadingStatus.COMPLETE,
        origin: 'PatientCommands.loadCurrentPatient',
        entity: patient,
      });

      return patient;
    } catch (error) {
      if (error instanceof Error) {
        this.stateProvider.setState(<PatientState>{
          loading: LoadingStatus.ERROR,
          origin: 'PatientCommands.loadCurrentPatient',
          message: error.message,
        });
      }
      throw error;
    }
  }

  initVerifier() {
    return this.authenticationCommands.initVerifier();
  }

  async signInWithPhoneNumber(
    phoneNumber: string,
    verifier: any,
  ): Promise<void> {
    this.stateProvider.setState(<PatientState>{
      loading: LoadingStatus.LOADING,
      origin: 'PatientCommands.signInWithPhoneNumber',
    });
    try {
      const confirmResult =
        await this.authenticationCommands.signInWithPhoneNumber(
          phoneNumber,
          verifier,
        );
      this.stateProvider.setState(<PatientState>{
        loading: LoadingStatus.WAITING,
        origin: 'PatientCommands.signInWithPhoneNumber',
      });
      this.eventProvider.dispatch(new PatientSentOtp(confirmResult));
    } catch (e: any) {
      let message = e.message; //"Une erreur s'est produite";
      if (e instanceof FirebaseError) {
        if (e.code === 'auth/invalid-phone-number') {
          message = 'N° de téléphone invalide';
        } else if (e.code === 'auth/captcha-check-failed') {
          message = 'La réponse reCAPTCHA est invalide';
        } else if (e.code === 'auth/missing-phone-number') {
          message = 'N° de téléphone invalide';
        } else if (e.code === 'auth/quota-exceeded') {
          message =
            "Une erreur technique s'est produite, veuillez essayer à nouveau dans quelques instants";
        } else if (e.code === 'auth/user-disabled') {
          message = 'N° de téléphone désactivé';
        } else if (e.code === 'auth/operation-not-allowed') {
          message = 'Opération interdite';
        }
      }
      this.stateProvider.setState(<PatientState>{
        loading: LoadingStatus.ERROR,
        origin: 'PatientCommands.signInWithPhoneNumber',
        message,
      });
    }
  }

  async verifySignInWithPhoneNumber(code: string, confirmResult: any) {
    this.stateProvider.setState(<PatientState>{
      loading: LoadingStatus.LOADING,
      origin: 'PatientCommands.verifySignInWithPhoneNumber',
    });
    const user = await this.authenticationCommands.verifySignInWithPhoneNumber(
      code,
      confirmResult,
    );
    this.loggerRepository.who = user.id.toString();
    try {
      const patient = await this.repository.postLoginAction();
      patient.userId = UserId.create(new UniqueEntityID(user.id.toString()));
      this.stateProvider.setState(<PatientState>{
        loading: LoadingStatus.COMPLETE,
        origin: 'PatientCommands.verifySignInWithPhoneNumber',
        entity: patient,
      });
      this.eventProvider.dispatch(new PatientSignedIn(patient));
    } catch (e) {
      console.error(e);
      this.stateProvider.setState(<PatientState>{
        loading: LoadingStatus.ERROR,
        origin: 'PatientCommands.verifySignInWithPhoneNumber',
        message: 'Le patient est introuvable !',
      });
    }
  }

  async verifySignInWithPhoneNumberAndEmail(
    code: string,
    resultAlternativeLogin: ResultAlternativeLoginAction,
  ) {
    const user =
      await this.authenticationCommands.verifySignInWithPhoneNumberAndEmail(
        code,
        resultAlternativeLogin,
      );
    this.loggerRepository.who = user.id.toString();
    try {
      const patient = await this.repository.postLoginAction();
      patient.userId = UserId.create(new UniqueEntityID(user.id.toString()));
      this.stateProvider.setState(<PatientState>{
        loading: LoadingStatus.COMPLETE,
        origin: 'PatientCommands.verifySignInWithPhoneNumberAndEmail',
        entity: patient,
      });
      this.eventProvider.dispatch(new PatientSignedIn(patient));
    } catch (e) {
      console.error(e);
      this.stateProvider.setState(<PatientState>{
        loading: LoadingStatus.ERROR,
        origin: 'PatientCommands.verifySignInWithPhoneNumberAndEmail',
        message: 'Le patient est introuvable !',
      });
    }
  }

  async signOut() {
    let patient: Patient | undefined;
    try {
      patient = await this.getCurrentPatient();
      // eslint-disable-next-line no-empty
    } catch (error: unknown) {}
    if (patient != null) {
      this.eventProvider.dispatch(new PatientSignedOut(patient));
    }

    await this.authenticationCommands.signOut();

    this.stateProvider.setState(<PatientState>{
      loading: LoadingStatus.COMPLETE,
      origin: 'PatientCommands.signOut',
    });
  }

  async getCurrentPatient(): Promise<Patient | undefined> {
    const user = await this.authenticationCommands.getCurrentPatientUser();
    if (user == null) {
      return undefined;
    }
    return this.repository.loadByUserId(user.userId.id.toString());
  }

  async updateToken(token: string | undefined) {
    if (token) {
      const patient = await this.getCurrentPatient();
      if (patient) {
        patient.notificationWebToken = token;
        await this.repository.save(patient);
        await this.loggerRepository.debugCloud(
          'PatientCommand',
          'updateToken',
          {
            props: patient.props,
            id: patient.id.toString(),
          },
        );
      }
    }
  }

  async updateByPatient(patient: Patient, notify = true): Promise<Patient> {
    this.stateProvider.setState(<PatientState>{
      loading: LoadingStatus.LOADING,
      entity: this.stateProvider.state.entity,
    });
    try {
      await this.repository.save(patient);
      await this.loggerRepository.debugCloud(
        'PatientCommand',
        'updateByPatient',
        {
          props: patient.props,
          id: patient.id.toString(),
        },
      );
      this.stateProvider.setState(<PatientState>{
        loading: LoadingStatus.COMPLETE,
        entity: patient,
      });

      this.eventProvider.dispatch(new PatientUpdated(patient, notify));
      return patient;
    } catch (error) {
      if (error instanceof Error) {
        this.stateProvider.setState(<PatientState>{
          loading: LoadingStatus.ERROR,
          message: error.message,
          entity: patient,
        });
      }
      throw error;
    }
  }

  async updatePictureByPatient(patient: Patient, file: File) {
    if (patient.userId) {
      let picture = Picture.create({
        file: file,
        path: `/users/${patient.userId.id.toString()}/picture`,
      });
      picture = await this.pictureRepository.save(picture);
      patient.avatarUrl = picture.url;
      await this.updateByPatient(patient, true);
    }
  }

  async closeAccount() {
    await this.repository.closeAccount();
  }

  async checkPhoneNumber(phoneNumber: string) {
    return this.repository.checkPhoneNumber(phoneNumber);
  }

  async postAlternativeLoginAction(
    prefix: string,
    phoneNumber: string,
    email: string,
  ) {
    const result = await this.repository.postAlternativeLoginAction(
      prefix,
      phoneNumber,
      email,
    );
    this.eventProvider.dispatch(new PatientSentOtp(result));
    return result;
  }

  async createGoal(goal: Goal) {
    const created = await this.goalRepository.create(goal);
    this.goalEventProvider.dispatch(new GoalCreated(created));
  }

  async updateGoal(goal: Goal) {
    const updated = await this.goalRepository.update(goal);
    this.goalEventProvider.dispatch(new GoalUpdated(updated));
  }

  async changeAchievementGoal(goal: Goal) {
    const updated = await this.goalRepository.update(goal);
    this.goalEventProvider.dispatch(new GoalChangeAchievement(updated));
  }

  async findAllGoal(patientId: string, status?: GoalStatus): Promise<Goal[]> {
    return this.goalRepository.findAll(patientId, status);
  }

  async migrateGoal(patient: Patient) {
    if (patient.goals && patient.goals.length > 0) {
      for (let i = patient.goals.length - 1; i >= 0; i--) {
        const newGoal = Goal.create({
          patientId: patient.id.toString(),
          patientUserId: patient.userId?.id.toString(),
          description: patient.goals[i],
          status: GoalStatus.INITIAL,
          achieved: false,
          createdAt: patient.updatedAt
            ? patient.updatedAt
            : patient.createdAt
            ? patient.createdAt
            : new Date(),
          updatedAt: undefined,
        });
        await this.goalRepository.create(newGoal);
        patient.goals.splice(i, 1);
      }
      await this.repository.save(patient);
    }
  }
}
